import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Merge, CheckCircle2, Crown, Archive, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Manual duplicate-product merge dialog.
 * Flow: admin picks a group → picks the winner → confirms the parsed mg for
 * each loser (with editable override) → hits "Merge". Losers get archived
 * (active=false) and their mg-variants are appended to the winner.
 */
export default function DuplicateMergeDialog({ open, onOpenChange, onMerged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const [winnerId, setWinnerId] = useState(null);
  const [mgOverrides, setMgOverrides] = useState({}); // id -> mg string
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setSelectedGroupKey(null);
    setWinnerId(null);
    setMgOverrides({});
    setLoading(true);
    api.get("/admin/products/duplicate-groups")
      .then(({ data }) => setGroups(data?.groups || []))
      .catch(() => toast.error("Failed to load duplicate groups"))
      .finally(() => setLoading(false));
  }, [open]);

  const currentGroup = groups.find((g) => g.base_name === selectedGroupKey);
  const loserIds = currentGroup
    ? currentGroup.members.filter((m) => m.id !== winnerId).map((m) => m.id)
    : [];

  const doMerge = async () => {
    if (!winnerId || loserIds.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/merge-duplicates", {
        winner_id: winnerId,
        loser_ids: loserIds,
        mg_overrides: mgOverrides,
      });
      setResult(data);
      toast.success(`Merged: ${data.added_variants.length} variant${data.added_variants.length === 1 ? "" : "s"} added, ${data.archived_ids.length} archived`);
      onMerged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Merge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-3xl bg-bone border-borderSubtle max-h-[85vh] overflow-y-auto" data-testid="duplicate-merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <Merge size={20} strokeWidth={2}/> Merge duplicate products
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-stone mb-4">
          Pick a winner in a group — the other products become <b>mg-variants</b> on the winner, then are archived.
        </p>

        {result ? (
          <div className="border border-borderSubtle bg-white p-4" data-testid="merge-result">
            <p className="font-serif text-lg mb-3 inline-flex items-center gap-2 text-oxblood">
              <CheckCircle2 size={16} strokeWidth={2}/> Merge complete
            </p>
            <div className="text-sm space-y-2">
              <p><span className="font-mono">{result.added_variants.length}</span> mg-variant(s) added to the winner</p>
              <p><span className="font-mono">{result.archived_ids.length}</span> product(s) archived</p>
              <p><span className="font-mono">{result.final_variant_count}</span> total variants now on winner</p>
              {(result.slug_renamed || result.name_renamed) && (
                <div className="mt-3 border-l-2 border-emerald-600 pl-3" data-testid="merge-rename-notice">
                  <p className="text-[10px] uppercase tracking-widest font-mono text-emerald-700 mb-1">Auto-cleaned</p>
                  {result.name_renamed && (
                    <p className="text-xs text-stone">Name → <span className="font-mono">{result.name_renamed}</span></p>
                  )}
                  {result.slug_renamed && (
                    <p className="text-xs text-stone">Slug → <span className="font-mono">{result.slug_renamed}</span></p>
                  )}
                </div>
              )}
              {result.skipped?.length > 0 && (
                <div className="mt-3 border-l-2 border-oxblood pl-3">
                  <p className="text-[10px] uppercase tracking-widest font-mono text-oxblood mb-1">Skipped</p>
                  {result.skipped.map((s, i) => (
                    <p key={i} className="text-xs text-stone">
                      {s.name || s.id}: {s.reason}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-charcoal text-bone tracking-[0.2em] text-xs uppercase hover:bg-oxblood transition-colors"
              data-testid="merge-close-btn"
            >
              Done
            </button>
          </div>
        ) : loading ? (
          <p className="overline text-stone">Loading duplicate groups…</p>
        ) : groups.length === 0 ? (
          <div className="border border-borderSubtle bg-white p-6 text-center" data-testid="merge-no-duplicates">
            <p className="text-sm text-stone">No duplicate product groups found. All products have unique names.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Group picker */}
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone mb-2 font-mono">1. Pick a group</label>
              <div className="space-y-2" data-testid="duplicate-groups-list">
                {groups.map((g) => (
                  <button
                    key={g.base_name}
                    type="button"
                    onClick={() => { setSelectedGroupKey(g.base_name); setWinnerId(null); setMgOverrides({}); }}
                    className={`w-full text-left rounded-2xl border px-4 py-3 transition-colors ${
                      selectedGroupKey === g.base_name
                        ? "border-oxblood bg-white"
                        : "border-borderSubtle bg-white hover:border-charcoal"
                    }`}
                    data-testid={`duplicate-group-${g.base_name.replace(/\s+/g, "-")}`}
                  >
                    <p className="font-serif text-base capitalize">{g.base_name}</p>
                    <p className="text-[11px] text-stone font-mono uppercase tracking-widest mt-0.5">
                      {g.members.length} products · {g.members.map((m) => m.parsed_mg ? `${m.parsed_mg}mg` : "?").join(" · ")}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Winner picker + mg overrides */}
            {currentGroup && (
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-stone mb-2 font-mono">2. Choose the winner (kept) + confirm each mg</label>
                <div className="border border-borderSubtle bg-white p-4 space-y-3" data-testid="merge-members">
                  {currentGroup.members.map((m) => {
                    const isWinner = winnerId === m.id;
                    const mgValue = mgOverrides[m.id] ?? m.parsed_mg ?? "";
                    return (
                      <div
                        key={m.id}
                        className={`grid grid-cols-[auto_1fr_120px_auto] gap-3 items-center rounded-xl p-3 transition-colors ${
                          isWinner ? "bg-oxblood/5 ring-1 ring-oxblood" : "bg-bone/40"
                        }`}
                        data-testid={`merge-member-${m.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => setWinnerId(m.id)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono transition-colors ${
                            isWinner ? "bg-oxblood text-bone" : "bg-charcoal/10 text-charcoal hover:bg-charcoal hover:text-bone"
                          }`}
                          data-testid={`pick-winner-${m.id}`}
                        >
                          {isWinner ? <><Crown size={12} strokeWidth={2}/> Winner</> : "Keep this"}
                        </button>
                        <div className="min-w-0">
                          <p className="font-serif text-sm truncate">{m.name}</p>
                          <p className="text-[10px] text-stone font-mono uppercase tracking-widest">
                            {m.sku} · ${(m.price ?? 0).toFixed(2)}{m.existing_variant_count > 0 ? ` · ${m.existing_variant_count} existing mg-variants` : ""}
                          </p>
                        </div>
                        <Input
                          type="text"
                          placeholder="mg"
                          value={mgValue}
                          onChange={(e) => setMgOverrides({ ...mgOverrides, [m.id]: e.target.value })}
                          className="rounded-xl border-borderSubtle bg-white text-sm font-mono"
                          data-testid={`merge-mg-override-${m.id}`}
                        />
                        {!isWinner && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-stone font-mono uppercase tracking-widest" title="Will be archived">
                            <Archive size={11} strokeWidth={2}/> Archive
                          </span>
                        )}
                        {isWinner && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-oxblood font-mono uppercase tracking-widest">
                            <CheckCircle2 size={11} strokeWidth={2}/> Keep
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!winnerId && (
                  <p className="mt-2 text-xs text-stone inline-flex items-center gap-1.5">
                    <AlertCircle size={12} strokeWidth={2}/> Pick which product should keep its slug and become the merged parent.
                  </p>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => onOpenChange(false)}
                    className="px-4 py-2 border border-borderSubtle text-charcoal tracking-[0.2em] text-xs uppercase hover:border-charcoal transition-colors"
                    data-testid="merge-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={doMerge}
                    disabled={!winnerId || loserIds.length === 0 || busy}
                    className="px-5 py-2 bg-oxblood text-bone tracking-[0.2em] text-xs uppercase hover:bg-charcoal transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    data-testid="merge-confirm-btn"
                  >
                    <Merge size={12} strokeWidth={2}/> {busy ? "Merging…" : `Merge ${loserIds.length} into 1`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
